import React, { useState, useEffect, useCallback } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronRight, ChevronDown, Clock, Leaf, Flame, Brain, Heart,
  Loader2, Star, Play, BookOpen, Droplets, Shield, Zap,
  ThumbsUp, ThumbsDown, MessageSquare
} from 'lucide-react';

const AXIS_ICONS = { energy: Zap, gut: Droplets, inflammation: Flame, neuro: Brain, detox: Shield, metabolic: Heart };
const AXIS_COLORS = { energy: '#D4A843', gut: '#4E7A6B', inflammation: '#C87B7B', neuro: '#5C6B8A', detox: '#9B7BC8', metabolic: '#C89B7B' };

// ═══════════════════════════════════════════════════════════════
// DAILY FLOW VISUALIZATION
// ═══════════════════════════════════════════════════════════════

function DailyFlow({ flow }) {
  if (!flow?.length) return null;
  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2" data-testid="daily-flow">
      {flow.map((f, i) => (
        <React.Fragment key={i}>
          {i > 0 && <ChevronRight className="w-3 h-3 text-[#EBE8E3] flex-shrink-0" />}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: f.color + '10' }}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: f.color }} />
            <span className="text-[10px] font-['Work_Sans']" style={{ color: f.color }}>{f.time}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MEAL CARD
// ═══════════════════════════════════════════════════════════════

function MealCard({ meal, onOpen, onFeedback, onSwap }) {
  const [showWhy, setShowWhy] = useState(false);
  const mt = meal.meal_type_info || {};
  const why = meal.why_this_recipe || {};

  return (
    <div className="rounded-2xl border border-[#EBE8E3] overflow-hidden bg-white" data-testid={`meal-card-${meal.slot}`}>
      {/* Header */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-1">
          <Badge className="text-[9px] border-0 px-2 py-0.5" style={{ backgroundColor: (mt.color || '#8A8681') + '15', color: mt.color }}>
            {mt.label || meal.meal_type}
          </Badge>
          <span className="text-[10px] text-[#8A8681] font-['Work_Sans']">{mt.time}</span>
        </div>
        <h3 className="text-base font-['Outfit'] font-medium text-[#2C2A29] mt-2">{meal.recipe?.name}</h3>
        <div className="flex items-center gap-2 mt-1">
          {meal.meal_intensity && meal.meal_intensity !== 'moderate' && (
            <Badge variant="outline" className={`text-[8px] ${
              meal.meal_intensity === 'light' ? 'border-[#4E7A6B]/20 text-[#4E7A6B]' :
              meal.meal_intensity === 'strong_intervention' ? 'border-[#C87B7B]/20 text-[#C87B7B]' :
              'border-[#8A8681]/20 text-[#8A8681]'
            }`}>
              {meal.meal_intensity === 'strong_intervention' ? 'targeted intervention' : meal.meal_intensity}
            </Badge>
          )}
          {meal.recipe_match && (
            <Badge variant="outline" className={`text-[8px] ${
              meal.recipe_match.level === 'high' ? 'border-[#4E7A6B]/20 text-[#4E7A6B]' :
              meal.recipe_match.level === 'good' ? 'border-[#D4A843]/20 text-[#D4A843]' :
              'border-[#5C6B8A]/20 text-[#5C6B8A]'
            }`} data-testid={`match-badge-${meal.slot}`}>
              {meal.recipe_match.level === 'high' ? 'Highly matched' : meal.recipe_match.level === 'good' ? 'Good option' : 'General safe'}
            </Badge>
          )}
        </div>
        {meal.timing_note && (
          <p className="text-[10px] text-[#C87B7B] font-['Work_Sans'] mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={1.5} />{meal.timing_note}
          </p>
        )}
      </div>

      {/* Bio scores mini-bar */}
      {meal.bio_scores && (
        <div className="px-5 pb-2 flex gap-2">
          {Object.entries(meal.bio_scores).filter(([_, v]) => v >= 6).map(([axis, score]) => {
            const Icon = AXIS_ICONS[axis] || Star;
            return (
              <div key={axis} className="flex items-center gap-1" title={`${axis}: ${score}/10`}>
                <Icon className="w-3 h-3" style={{ color: AXIS_COLORS[axis] }} strokeWidth={1.5} />
                <span className="text-[9px] font-mono" style={{ color: AXIS_COLORS[axis] }}>{score}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* WHY this recipe — 3-layer */}
      <div className="px-5 pb-3">
        <button onClick={() => setShowWhy(!showWhy)} className="flex items-center gap-1.5 text-[10px] text-[#8C6B55]/60 font-['Work_Sans'] hover:text-[#8C6B55] transition-colors" data-testid={`why-toggle-${meal.slot}`}>
          <BookOpen className="w-3 h-3" strokeWidth={1.5} />
          Why this meal
          {showWhy ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
        </button>
        {showWhy && (
          <div className="mt-2 p-3 rounded-xl bg-[#F8F6F3] space-y-1.5 animate-in fade-in slide-in-from-top-1 duration-200" data-testid={`why-block-${meal.slot}`}>
            <p className="text-xs text-[#8C6B55] font-['Work_Sans'] font-medium">{why.state}</p>
            <p className="text-[11px] text-[#5C5955] font-['Work_Sans'] leading-relaxed">{why.mechanism}</p>
            <p className="text-[11px] text-[#4E7A6B] font-['Work_Sans']">{why.outcome}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-5 pb-4 flex items-center gap-3">
        <button onClick={() => onOpen(meal)} className="text-xs text-[#5C6B8A] font-['Work_Sans'] font-medium hover:text-[#4C5B7A] transition-colors" data-testid={`view-recipe-${meal.slot}`}>
          View Recipe
        </button>
        <button onClick={() => onSwap(meal)} className="text-[10px] text-[#C89B7B] font-['Work_Sans'] hover:text-[#8C6B55] transition-colors" data-testid={`swap-${meal.slot}`}>
          Swap Meal
        </button>
        <div className="flex-1" />
        <button onClick={() => onFeedback(meal)} className="text-[10px] text-[#8A8681] font-['Work_Sans'] hover:text-[#5C5955] flex items-center gap-1 transition-colors" data-testid={`feedback-${meal.slot}`}>
          <MessageSquare className="w-3 h-3" /> Feedback
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RECIPE DETAIL
// ═══════════════════════════════════════════════════════════════

function RecipeDetail({ recipe, onBack }) {
  if (!recipe) return null;
  const why = recipe.why_this_recipe || {};
  const bio = recipe.bio_scores || {};

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6" data-testid="recipe-detail">
      <button onClick={onBack} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] hover:text-[#5C5955] font-['Work_Sans'] flex items-center gap-1">
        <ChevronRight className="w-3 h-3 rotate-180" /> Back
      </button>

      <div>
        <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] capitalize mb-2">{recipe.category}</Badge>
        <h1 className="text-2xl font-['Outfit'] font-light text-[#2C2A29]">{recipe.name}</h1>
        <p className="text-sm text-[#5C5955] font-['Work_Sans'] mt-1">{recipe.base_description}</p>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-[#8A8681] font-['Work_Sans']">
          <span><Clock className="w-3 h-3 inline mr-1" strokeWidth={1.5} />{recipe.prep_time}</span>
          <span className="capitalize">{recipe.difficulty}</span>
        </div>
      </div>

      {/* Why this recipe — 3 layer */}
      {why.state && (
        <div className="p-4 rounded-2xl border-l-[3px] border-[#8C6B55]/30 bg-[#8C6B55]/[0.03]" data-testid="recipe-why">
          <p className="text-[10px] uppercase tracking-[0.15em] text-[#8C6B55]/50 font-['Work_Sans'] mb-2">Why This Recipe</p>
          <p className="text-sm text-[#8C6B55] font-['Work_Sans'] font-medium mb-1">{why.state}</p>
          <p className="text-xs text-[#5C5955] font-['Work_Sans'] leading-relaxed mb-1">{why.mechanism}</p>
          <p className="text-xs text-[#4E7A6B] font-['Work_Sans']">{why.outcome}</p>
        </div>
      )}

      {/* Bio scores */}
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(bio).map(([axis, score]) => {
          const Icon = AXIS_ICONS[axis] || Star;
          const color = AXIS_COLORS[axis] || '#8A8681';
          return (
            <div key={axis} className="rounded-xl border border-[#EBE8E3] p-3 text-center">
              <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} strokeWidth={1.25} />
              <p className="text-lg font-['Outfit'] font-light" style={{ color }}>{score}</p>
              <p className="text-[9px] uppercase tracking-wider text-[#8A8681] capitalize">{axis}</p>
            </div>
          );
        })}
      </div>

      {/* Ingredients */}
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Ingredients</p>
        <div className="space-y-2">
          {(recipe.ingredients || []).map((ing, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5">
              <Leaf className="w-3 h-3 text-[#4E7A6B]" strokeWidth={1.5} />
              <span className="text-sm text-[#2C2A29] font-['Work_Sans'] flex-1">{ing.replaced ? <><s className="text-[#8A8681]">{ing.original}</s> → {ing.name}</> : ing.name}</span>
              <span className="text-xs text-[#8A8681] font-['Work_Sans']">{ing.qty}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Steps */}
      {recipe.steps?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Preparation</p>
          <div className="space-y-3">
            {recipe.steps.map((step, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F4F2EE] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-medium text-[#8A8681]">{i + 1}</span>
                </div>
                <p className="text-sm text-[#4A4745] font-['Work_Sans'] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variants */}
      {recipe.variants?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8681] font-['Work_Sans'] mb-3">Variants</p>
          <div className="space-y-2">
            {recipe.variants.map((v, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#F8F6F3] border border-[#EBE8E3]">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[9px] capitalize">{v.variant_type?.replace('_', ' ')}</Badge>
                  {v.condition_tags?.map(t => <Badge key={t} variant="outline" className="border-[#EBE8E3] text-[#8A8681] text-[8px] capitalize">{t}</Badge>)}
                </div>
                <p className="text-xs text-[#5C5955] font-['Work_Sans']">{v.modifications}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FEEDBACK MODAL
// ═══════════════════════════════════════════════════════════════

function FeedbackPanel({ meal, onClose, onSubmit }) {
  const [form, setForm] = useState({ energy_response: 3, digestion_response: 3, post_meal_heaviness: 1, bloating: 1, bowel_change: 'none', symptom_shift: 'none', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit({ ...form, recipe_id: meal.recipe?.id, meal_slot: meal.slot });
      toast.success('Feedback recorded — this helps us learn your patterns');
      onClose();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const ScaleInput = ({ label, value, onChange, max = 5 }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-[#5C5955] font-['Work_Sans']">{label}</span>
        <span className="text-xs font-mono text-[#8A8681]">{value}/{max}</span>
      </div>
      <div className="flex gap-1">
        {Array.from({ length: max }, (_, i) => (
          <button key={i} onClick={() => onChange(i + 1)} type="button"
            className={`flex-1 h-2 rounded-full transition-colors ${i < value ? 'bg-[#C89B7B]' : 'bg-[#EBE8E3]'}`} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-[#EBE8E3] p-5 bg-white space-y-4" data-testid="feedback-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">Meal Feedback: {meal.recipe?.name}</p>
        <button onClick={onClose} className="text-[#8A8681] hover:text-[#5C5955]"><ChevronDown className="w-4 h-4" /></button>
      </div>
      <ScaleInput label="Energy after meal" value={form.energy_response} onChange={v => setForm(p => ({ ...p, energy_response: v }))} />
      <ScaleInput label="Digestion comfort" value={form.digestion_response} onChange={v => setForm(p => ({ ...p, digestion_response: v }))} />
      <ScaleInput label="Post-meal heaviness" value={form.post_meal_heaviness} onChange={v => setForm(p => ({ ...p, post_meal_heaviness: v }))} />
      <ScaleInput label="Bloating" value={form.bloating} onChange={v => setForm(p => ({ ...p, bloating: v }))} />
      <div className="grid grid-cols-2 gap-3">
        <div>
          <span className="text-xs text-[#5C5955] font-['Work_Sans'] block mb-1">Bowel change</span>
          <div className="flex gap-1">
            {['none', 'improved', 'worsened'].map(o => (
              <button key={o} onClick={() => setForm(p => ({ ...p, bowel_change: o }))} type="button"
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-['Work_Sans'] transition-colors ${form.bowel_change === o ? 'bg-[#C89B7B]/10 text-[#C89B7B] border border-[#C89B7B]/20' : 'bg-[#F4F2EE] text-[#8A8681]'}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
        <div>
          <span className="text-xs text-[#5C5955] font-['Work_Sans'] block mb-1">Symptom shift</span>
          <div className="flex gap-1">
            {['none', 'improved', 'worsened'].map(o => (
              <button key={o} onClick={() => setForm(p => ({ ...p, symptom_shift: o }))} type="button"
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-['Work_Sans'] transition-colors ${form.symptom_shift === o ? 'bg-[#C89B7B]/10 text-[#C89B7B] border border-[#C89B7B]/20' : 'bg-[#F4F2EE] text-[#8A8681]'}`}>
                {o}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Any additional notes..." className="bg-white border-[#EBE8E3] text-[#2C2A29] text-xs min-h-[50px]" />
      <Button onClick={handleSubmit} disabled={saving} className="w-full bg-[#2C2A29] text-white hover:bg-[#3C3A39] rounded-full" data-testid="submit-feedback">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Feedback'}
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export default function NutrientKitchen() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRecipe, setActiveRecipe] = useState(null);
  const [feedbackMeal, setFeedbackMeal] = useState(null);
  const [goalOverride, setGoalOverride] = useState(null);
  const [swapMeal, setSwapMeal] = useState(null);
  const [swapOptions, setSwapOptions] = useState([]);
  const [adherence, setAdherence] = useState(null);

  const fetchPlan = useCallback(async () => {
    try {
      const params = goalOverride ? `?goal=${goalOverride}` : '';
      const res = await axios.get(`${API}/nix/daily-plan${params}`);
      setPlan(res.data);
    } catch {} finally { setLoading(false); }
  }, [goalOverride]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  useEffect(() => {
    axios.get(`${API}/nix/adherence`).then(r => setAdherence(r.data)).catch(() => {});
  }, []);

  const openRecipe = async (meal) => {
    try {
      const res = await axios.get(`${API}/nix/recipes/${meal.recipe.id}`);
      setActiveRecipe({ ...res.data, ingredients: meal.ingredients || res.data.ingredients });
    } catch { toast.error('Recipe not found'); }
  };

  const submitFeedback = async (data) => {
    await axios.post(`${API}/nix/feedback`, data);
  };

  const handleSwap = async (meal) => {
    setSwapMeal(meal);
    try {
      const res = await axios.get(`${API}/nix/swap/${meal.recipe.id}?slot=${meal.slot}`);
      setSwapOptions(res.data?.alternatives || []);
    } catch { setSwapOptions([]); }
  };

  if (activeRecipe) {
    return (
      <BiologicalLayout>
        <RecipeDetail recipe={activeRecipe} onBack={() => setActiveRecipe(null)} />
      </BiologicalLayout>
    );
  }

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-4 py-6" data-testid="nutrient-kitchen">
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A8681] font-['Work_Sans'] mb-2">Nutrient Intelligence Kitchen</p>
          <h1 className="text-xl sm:text-2xl font-['Outfit'] font-light text-[#2C2A29]">Today's Biological Nutrition</h1>
          {plan?.bio_state_summary && (
            <p className="text-xs text-[#8C6B55] font-['Work_Sans'] mt-1 italic">
              Optimized for: {plan.bio_state_summary.priorities?.map(p => p.replace('_', ' ')).join(', ')}
            </p>
          )}
        </div>

        {/* Daily Flow */}
        {plan?.daily_flow && <DailyFlow flow={plan.daily_flow} />}

        {/* Starter Experience (first-time user) */}
        {plan?.is_first_experience && plan?.starter_message && (
          <div className="mt-3 p-5 rounded-2xl bg-[#4E7A6B]/[0.04] border border-[#4E7A6B]/10" data-testid="starter-experience">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#4E7A6B]/60 font-['Work_Sans'] mb-2">Your First Food Experience</p>
            <p className="text-sm text-[#4E7A6B] font-['Work_Sans'] leading-relaxed">{plan.starter_message}</p>
            <p className="text-[10px] text-[#4E7A6B]/50 font-['Work_Sans'] mt-2">After each meal, share how your body responded to help the system learn.</p>
          </div>
        )}

        {/* Daily Insight */}
        {plan?.daily_insight && (
          <div className="mt-3 px-4 py-3 rounded-xl bg-[#8C6B55]/[0.04] border-l-2 border-[#8C6B55]/20" data-testid="daily-insight">
            <p className="text-xs text-[#8C6B55] font-['Work_Sans'] leading-relaxed">{plan.daily_insight}</p>
            {plan.state_confidence != null && (
              <div className="flex items-center gap-2 mt-1.5" title="Confidence improves as system learns your biology">
                <span className="text-[9px] text-[#8A8681] font-['Work_Sans']">System confidence</span>
                <div className="w-16 h-1.5 bg-[#EBE8E3] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.round(plan.state_confidence * 100)}%`, backgroundColor: plan.state_confidence >= 0.6 ? '#4E7A6B' : plan.state_confidence >= 0.3 ? '#D4A843' : '#C87B7B' }} />
                </div>
                <Badge variant="outline" className={`text-[8px] ${
                  plan.confidence_mode === 'precise' ? 'border-[#4E7A6B]/30 text-[#4E7A6B]' :
                  plan.confidence_mode === 'balanced' ? 'border-[#D4A843]/30 text-[#D4A843]' :
                  'border-[#C87B7B]/30 text-[#C87B7B]'
                }`}>
                  {plan.confidence_mode === 'precise' ? 'Data-rich, personalized' : plan.confidence_mode === 'balanced' ? 'Partially personalized' : 'Safe mode'}
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Goal Override */}
        <div className="flex gap-1.5 mt-4 mb-6 overflow-x-auto">
          {[null, 'energy_boost', 'gut_repair', 'anti_inflammatory', 'neuro_stable', 'calming'].map(g => (
            <button key={g || 'auto'} onClick={() => { setGoalOverride(g); setLoading(true); }}
              className={`px-3 py-1.5 rounded-full text-[10px] font-['Work_Sans'] whitespace-nowrap transition-colors ${
                goalOverride === g ? 'bg-[#8C6B55]/10 text-[#8C6B55]' : 'text-[#8A8681] hover:text-[#5C5955] bg-[#F4F2EE]'
              }`} data-testid={`goal-${g || 'auto'}`}>
              {g ? g.replace('_', ' ') : 'Auto (state-based)'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#C89B7B] animate-spin" /></div>
        ) : (
          <div className="space-y-4" data-testid="meal-cards">
            {plan?.meals?.map(meal => (
              <MealCard key={meal.slot} meal={meal} onOpen={openRecipe} onFeedback={setFeedbackMeal} onSwap={handleSwap} />
            ))}
          </div>
        )}

        {/* Feedback Panel */}
        {feedbackMeal && (
          <div className="mt-6">
            <FeedbackPanel meal={feedbackMeal} onClose={() => setFeedbackMeal(null)} onSubmit={submitFeedback} />
          </div>
        )}

        {/* Swap Panel */}
        {swapMeal && swapOptions.length > 0 && (
          <div className="mt-4 rounded-2xl border border-[#EBE8E3] p-5 bg-white" data-testid="swap-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-['Work_Sans'] font-medium text-[#2C2A29]">Alternatives for {swapMeal.recipe?.name}</p>
              <button onClick={() => { setSwapMeal(null); setSwapOptions([]); }} className="text-[#8A8681] hover:text-[#5C5955]"><ChevronDown className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {swapOptions.map((opt, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-[#F8F6F3] border border-[#EBE8E3]" data-testid={`swap-option-${i}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-['Work_Sans'] text-[#2C2A29]">{opt.recipe?.name}</p>
                      <Badge variant="outline" className="text-[8px] border-[#EBE8E3] text-[#8A8681]">{opt.swap_label}</Badge>
                    </div>
                    <p className="text-[10px] text-[#5C5955] font-['Work_Sans']">{opt.why_this_recipe?.state}</p>
                  </div>
                  <span className="text-[10px] text-[#8A8681]">{opt.recipe?.prep_time}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Adherence Score */}
        {adherence && adherence.meals_tracked > 0 && (
          <div className="mt-6 rounded-xl border border-[#EBE8E3] p-4 flex items-center gap-4" data-testid="adherence-score">
            <div className="text-center">
              <p className="text-2xl font-['Outfit'] font-light" style={{ color: adherence.adherence_score >= 70 ? '#4E7A6B' : adherence.adherence_score >= 40 ? '#D4A843' : '#C87B7B' }}>
                {adherence.adherence_score}%
              </p>
              <p className="text-[9px] uppercase tracking-wider text-[#8A8681]">Adherence</p>
            </div>
            <div className="flex-1">
              <div className="w-full h-2 bg-[#EBE8E3] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${adherence.adherence_score}%`, backgroundColor: adherence.adherence_score >= 70 ? '#4E7A6B' : adherence.adherence_score >= 40 ? '#D4A843' : '#C87B7B' }} />
              </div>
              <p className="text-[10px] text-[#8A8681] font-['Work_Sans'] mt-1">{adherence.meals_tracked} of {adherence.meals_expected} meals tracked this week</p>
            </div>
          </div>
        )}
      </div>
    </BiologicalLayout>
  );
}
